import type { BufferGeometry } from 'three';
import type { Ring, Vec2, Vec3 } from '../../geo/types';
import { ensureCCW, signedArea } from '../../geo/polygon';
import { fromRing, union } from '../../geo/clip';

/** Two levels, in world metres, between which a passing body takes up room. */
export interface Levels {
  readonly from: number;
  readonly to: number;
}

// An upright face has no plan area of its own, so it stands in the plan as a strip this wide.
const UPRIGHT_STRIP_M = 0.04;
const MIN_AREA_M2 = 1e-4;

/* The part of a triangle on the kept side of one level: Sutherland-Hodgman against a single
   horizontal plane. */
function cut(points: readonly Vec3[], level: number, keepAbove: boolean): readonly Vec3[] {
  const kept = (p: Vec3) => (keepAbove ? p[1] >= level : p[1] <= level);
  return points.flatMap((a, i) => {
    const b = points[(i + 1) % points.length]!;
    if (kept(a) === kept(b)) return kept(a) ? [a] : [];
    const t = (level - a[1]) / (b[1] - a[1]);
    const crossing: Vec3 = [a[0] + (b[0] - a[0]) * t, level, a[2] + (b[2] - a[2]) * t];
    return kept(a) ? [a, crossing] : [crossing];
  });
}

function strip(plan: readonly Vec2[]): Ring {
  let a = plan[0]!,
    b = plan[0]!,
    span = 0;
  for (const p of plan)
    for (const q of plan) {
      const d = Math.hypot(q[0] - p[0], q[1] - p[1]);
      if (d > span) [a, b, span] = [p, q, d];
    }
  const along: Vec2 = span > 0 ? [(b[0] - a[0]) / span, (b[1] - a[1]) / span] : [1, 0];
  const h = UPRIGHT_STRIP_M / 2;
  const [ux, uy] = [along[0] * h, along[1] * h];
  return [
    [a[0] - ux + uy, a[1] - uy - ux],
    [b[0] + ux + uy, b[1] + uy - ux],
    [b[0] + ux - uy, b[1] + uy + ux],
    [a[0] - ux - uy, a[1] - uy + ux],
  ];
}

function within(triangle: readonly Vec3[], levels: Levels): Ring | null {
  if (triangle.every((p) => p[1] < levels.from) || triangle.every((p) => p[1] > levels.to))
    return null;
  const kept = cut(cut(triangle, levels.from, true), levels.to, false);
  if (kept.length < 2) return null;
  const plan = kept.map((p): Vec2 => [p[0], -p[2]]);
  return Math.abs(signedArea(plan)) < MIN_AREA_M2 ? strip(plan) : ensureCCW(plan);
}

/** The plan of every part of a mesh that stands between two levels, for each pair of levels asked:
 *  what a body passing at those levels would strike. Read off the mesh itself, a pier, a pile or a
 *  low vault stops a boat exactly where it is drawn, and an opening lets it through. The mesh is in
 *  world coordinates. */
export function meshBarriers(
  geometry: BufferGeometry,
  bands: readonly Levels[]
): readonly (readonly Ring[])[] {
  const position = geometry.getAttribute('position');
  const index = geometry.index;
  const count = index ? index.count : position.count;
  const corner = (i: number): Vec3 => {
    const v = index ? index.getX(i) : i;
    return [position.getX(v), position.getY(v), position.getZ(v)];
  };
  const pieces = bands.map((): Ring[] => []);
  for (let i = 0; i < count; i += 3) {
    const triangle = [corner(i), corner(i + 1), corner(i + 2)];
    bands.forEach((levels, band) => {
      const piece = within(triangle, levels);
      if (piece) pieces[band]!.push(piece);
    });
  }
  return pieces.map((band) => union(band.map(fromRing)).map((piece) => piece.outer));
}
